export type RevenuesReportTab = 'subscriptions' | 'rentals' | 'products';

export type RevenuesReportSubscriptionStatusFilter =
  | '0'
  | '1'
  | '2'
  | '3'
  | '4'
  | '5'
  | '7'
  | '8';

export interface RevenuesReportFilters {
  date_from?: string;
  date_to?: string;
  tab?: RevenuesReportTab;
  page?: number;
  per_page?: number;
  top_limit?: number;
  search?: string;
  package?: string;
  payment_status?: string;
  subscription_status?: RevenuesReportSubscriptionStatusFilter | string;
}

export interface RevenuesReportSummarySection {
  count: number;
  amount: number;
  change_percent: number;
}

export interface RevenuesTopItem {
  name: string;
  amount: number;
  count: number;
}

export interface RevenuesTopSection {
  total_amount: number;
  items: RevenuesTopItem[];
}

export interface RevenuesReportRowStatus {
  id: number | string;
  name: string;
  name_en: string;
  badge_class: string;
  class?: string;
  key?: string;
  name_ar?: string;
}

export const REVENUES_REPORT_PAYMENT_STATUS = {
  PARTIAL: 0,
  PAID: 1,
  NONE: 2,
  REFUNDED: 3,
  PARTIALLY_REFUNDED: 4,
} as const;

export interface RevenuesReportTotalDisplay {
  show_approximation: boolean;
  primary: string;
  secondary: string | null;
}

export interface RevenuesReportRow {
  id: number;
  serial_number: number;
  subscription_id: number | null;
  player_id: number | null;
  player_name: string;
  customer_name?: string;
  item_name: string;
  package_id?: number | null;
  total: number;
  rounding_adjustment?: number | null;
  total_display?: RevenuesReportTotalDisplay | null;
  paid: number;
  paid_before_rounding?: number | null;
  paid_display?: RevenuesReportTotalDisplay | null;
  remaining: number;
  remaining_before_rounding?: number | null;
  remaining_display?: RevenuesReportTotalDisplay | null;
  refund_amount?: number;
  refund_amount_display?: RevenuesReportTotalDisplay | null;
  net_amount?: number;
  net_amount_display?: RevenuesReportTotalDisplay | null;
  sale_source?: string | null;
  operation_type?: string | null;
  start_date: string | null;
  end_date: string | null;
  created_at: string | null;
  status: RevenuesReportRowStatus;
  payment_status?: RevenuesReportRowStatus | null;
  subscription_status?: RevenuesReportRowStatus | null;
}

export interface RevenuesReportPagination {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  from: number | null;
  to: number | null;
}

export interface RevenuesReportPayload {
  filters: {
    date_from: string | null;
    date_to: string | null;
    tab: RevenuesReportTab;
    top_limit: number;
    search?: string | null;
    package?: string | null;
    payment_status?: string | null;
    subscription_status?: string | null;
  };
  summary: {
    total_amount: number;
    subscriptions: RevenuesReportSummarySection;
    rentals: RevenuesReportSummarySection;
    products: RevenuesReportSummarySection;
  };
  top: {
    activities: RevenuesTopSection;
    facilities: RevenuesTopSection;
    products: RevenuesTopSection;
  };
  table: {
    active_tab: RevenuesReportTab;
    counts: {
      subscriptions: number;
      rentals: number;
      products: number;
    };
    rows: RevenuesReportRow[];
    pagination: RevenuesReportPagination;
  };
  options?: {
    packages: Array<{ value: string; label: string }>;
    statuses: Array<{ value: string; label: string }>;
  };
}

export const createEmptyRevenuesReportPayload = (): RevenuesReportPayload => ({
  filters: {
    date_from: null,
    date_to: null,
    tab: 'subscriptions',
    top_limit: 7,
    search: null,
    package: null,
    payment_status: null,
    subscription_status: null,
  },
  summary: {
    total_amount: 0,
    subscriptions: {
      count: 0,
      amount: 0,
      change_percent: 0,
    },
    rentals: {
      count: 0,
      amount: 0,
      change_percent: 0,
    },
    products: {
      count: 0,
      amount: 0,
      change_percent: 0,
    },
  },
  top: {
    activities: {
      total_amount: 0,
      items: [],
    },
    facilities: {
      total_amount: 0,
      items: [],
    },
    products: {
      total_amount: 0,
      items: [],
    },
  },
  table: {
    active_tab: 'subscriptions',
    counts: {
      subscriptions: 0,
      rentals: 0,
      products: 0,
    },
    rows: [],
    pagination: {
      current_page: 1,
      last_page: 1,
      per_page: 10,
      total: 0,
      from: null,
      to: null,
    },
  },
  options: {
    packages: [],
    statuses: [
      { value: '0', label: 'SUBSCRIPTIONS.STATUS.ACTIVE' },
      { value: '1', label: 'SUBSCRIPTIONS.STATUS.INACTIVE' },
      { value: '7', label: 'SUBSCRIPTIONS.STATUS.FROZEN' },
      { value: '2', label: 'SUBSCRIPTIONS.STATUS.EXPIRED' },
      { value: '3', label: 'SUBSCRIPTIONS.STATUS.NEAR_EXPIRING' },
      { value: '4', label: 'SUBSCRIPTIONS.STATUS.SUSPENDED' },
      { value: '8', label: 'SUBSCRIPTIONS.STATUS.DRAFT' },
      { value: '5', label: 'SUBSCRIPTIONS.STATUS.CANCELED' },
    ],
  },
});
